import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Plus, Pencil, Trash2, BookOpen, Copy, Download, Workflow, Search } from 'lucide-react';
import ProtocolPanel from './ProtocolPanel';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { Checkbox } from '../ui/checkbox';

const CATEGORIES = ['Medical', 'Daily Care', 'Maintenance', 'Conditioning', 'Stats', 'Travel', 'Notes'];
const CATALOG_CATEGORIES = ['Medical', 'Daily Care', 'Maintenance', 'Conditioning'];

const CARE_TYPES = {
  'Medical':      ['Vaccination', 'Medication', 'Supplement', 'Procedure', 'Exam', 'Foaling', 'Other'],
  'Daily Care':   ['Feed', 'Water', 'Groom', 'Wraps & Packs', 'Order Supplies / Tack', 'Boarding'],
  'Maintenance':  ['Farrier', 'Dental', 'Chiropractic', 'Body Work / Massage', 'Other'],
  'Conditioning': ['Exercise', 'Training', 'Rehab', 'Other'],
  'Stats':        ['Weight', 'Height', 'Temperature', 'Vitals', 'Other'],
  'Travel':       ['Transport', 'Other'],
  'Notes':        ['General Note'],
};

const UNIT_OPTIONS = ['mL', 'cc', 'mg', 'g', 'oz', 'IU', 'scoop', 'flake', 'tablet', 'capsule', 'pump', 'other'];

const ACTIVE_TAB_COLORS = {
  'Medical':      { active: 'bg-purple-100 text-purple-700 border-purple-300',    inactive: 'bg-purple-50 text-purple-500 border-purple-200 hover:bg-purple-100' },
  'Daily Care':   { active: 'bg-sky-100 text-sky-700 border-sky-300',             inactive: 'bg-sky-50 text-sky-500 border-sky-200 hover:bg-sky-100' },
  'Maintenance':  { active: 'bg-yellow-100 text-yellow-700 border-yellow-300',    inactive: 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100' },
  'Conditioning': { active: 'bg-emerald-100 text-emerald-700 border-emerald-300', inactive: 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100' },
};
const INACTIVE_TAB = 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50';

export const FREQ_OPTIONS = [
  { value: 'multiple_daily', label: 'Multiple Times per Day' },
  { value: 'daily',          label: 'Daily' },
  { value: 'weekly',         label: 'Weekly' },
  { value: 'monthly',        label: 'Monthly' },
  { value: 'annually',       label: 'Annually' },
  { value: 'custom',         label: 'Other Interval' },
];

export const FREQ_DEFAULTS = {
  multiple_daily: { mode: 'specific_times', times: [''] },
  daily:          { all_day: false, time: '' },
  weekly:         { days: [], all_day: false, time: '' },
  monthly:        { day_type: 'numeric', day_number: 1, all_day: false, time: '' },
  annually:       { month: 'January', day: 1, all_day: false, time: '' },
  custom:         { interval: 1, unit: 'day', all_day: false, time: '' },
};

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS       = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const POSITIONS    = ['First','Second','Third','Fourth','Last'];
const FULL_DAYS    = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

function formatFreq(item) {
  if (!item.routine) return '—';
  const type = item.frequency_type;
  const c = item.frequency_config || {};
  if (!type) return '—';
  if (type === 'multiple_daily') return c.mode === 'interval' ? `Every ${c.interval_value||''} ${c.interval_unit||''}` : `${(c.times||[]).filter(Boolean).length || 1}× daily`;
  if (type === 'daily')     return c.all_day ? 'Daily' : `Daily${c.time ? ' ' + c.time : ''}`;
  if (type === 'weekly')    return `Weekly${(c.days||[]).length ? ' (' + c.days.join(', ') + ')' : ''}`;
  if (type === 'monthly')   return c.day_type === 'positional' ? `Monthly (${c.position||''} ${c.day_of_week||''})` : `Monthly (Day ${c.day_number||''})`;
  if (type === 'annually')  return `Annually (${c.month||''} ${c.day||''})`;
  if (type === 'custom')    return `Every ${c.interval||''} ${c.unit||''}${c.interval > 1 ? 's' : ''}`;
  return type;
}

// Time input — light gray text when empty, black when set (local time)
function TimeField({ value, onChange, label }) {
  return (
    <div className="flex items-center gap-1.5">
      {label && <span className="text-xs text-muted-foreground shrink-0">{label}</span>}
      <input
        type="time"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className={`h-7 text-xs border rounded px-2 w-28 focus:outline-none focus:ring-1 focus:ring-primary ${value ? 'time-filled' : ''}`}
      />
    </div>
  );
}

function AllDayOrTime({ fc, set }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
        <Checkbox checked={!!fc.all_day} onCheckedChange={(v) => set('all_day', v)} />
        All Day
      </label>
      {!fc.all_day && (
        <TimeField value={fc.time} onChange={v => set('time', v)} label="at" />
      )}
    </div>
  );
}

export function FrequencyConfig({ form, setForm }) {
  const fc = form.frequency_config || {};
  const set = (key, val) => setForm(f => ({ ...f, frequency_config: { ...f.frequency_config, [key]: val } }));

  switch (form.frequency_type) {
    case 'multiple_daily': return (
      <div className="space-y-2">
        <div className="flex gap-2">
          {['specific_times', 'interval'].map(m => (
            <button key={m} type="button"
              onClick={() => set('mode', m)}
              className={`text-xs px-2.5 py-1 rounded border transition-colors ${fc.mode === m || (!fc.mode && m === 'specific_times') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
            >
              {m === 'specific_times' ? 'Specific Times' : 'Every X Interval'}
            </button>
          ))}
        </div>
        {(fc.mode === 'interval') ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Every</span>
            <input type="number" min="1" max="1439"
              className="h-7 text-xs border rounded px-2 w-16"
              value={fc.interval_value || ''}
              onChange={e => set('interval_value', parseInt(e.target.value) || 1)}
            />
            <Select value={fc.interval_unit || 'hours'} onValueChange={v => set('interval_unit', v)}>
              <SelectTrigger className="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="minutes">Minutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-1.5">
            {(fc.times || ['']).map((t, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <TimeField value={t} onChange={v => { const times = [...(fc.times||[''])]; times[i] = v; set('times', times); }} />
                {(fc.times||['']).length > 1 && (
                  <button type="button" className="text-xs text-red-400 hover:text-red-600"
                    onClick={() => set('times', (fc.times||[]).filter((_,j) => j !== i))}>×</button>
                )}
              </div>
            ))}
            <button type="button" className="text-xs text-primary hover:underline"
              onClick={() => set('times', [...(fc.times||['']), ''])}>+ Add time</button>
          </div>
        )}
      </div>
    );

    case 'daily': return <AllDayOrTime fc={fc} set={set} />;

    case 'weekly': return (
      <div className="space-y-2">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Days</p>
          <div className="flex flex-wrap gap-1">
            {DAYS_OF_WEEK.map(d => (
              <button key={d} type="button"
                onClick={async () => {
                  const days = fc.days || [];
                  set('days', days.includes(d) ? days.filter(x => x !== d) : [...days, d]);
                }}
                className={`text-xs px-2 py-0.5 rounded border transition-colors ${(fc.days||[]).includes(d) ? 'bg-primary text-white border-primary' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
              >{d}</button>
            ))}
          </div>
        </div>
        <AllDayOrTime fc={fc} set={set} />
      </div>
    );

    case 'monthly': return (
      <div className="space-y-2">
        <div className="flex gap-2">
          {['numeric','positional'].map(m => (
            <button key={m} type="button"
              onClick={() => set('day_type', m)}
              className={`text-xs px-2.5 py-1 rounded border transition-colors ${fc.day_type === m || (!fc.day_type && m === 'numeric') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
            >
              {m === 'numeric' ? 'Day Number' : 'Day of Week'}
            </button>
          ))}
        </div>
        {(fc.day_type === 'positional') ? (
          <div className="flex gap-2 flex-wrap">
            <Select value={fc.position || 'First'} onValueChange={v => set('position', v)}>
              <SelectTrigger className="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
              <SelectContent>{POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={fc.day_of_week || 'Monday'} onValueChange={v => set('day_of_week', v)}>
              <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
              <SelectContent>{FULL_DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Day</span>
            <input type="number" min="1" max="31"
              className="h-7 text-xs border rounded px-2 w-16"
              value={fc.day_number || 1}
              onChange={e => set('day_number', Math.min(31, Math.max(1, parseInt(e.target.value)||1)))}
            />
          </div>
        )}
        <AllDayOrTime fc={fc} set={set} />
      </div>
    );

    case 'annually': return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={fc.month || 'January'} onValueChange={v => set('month', v)}>
            <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <input type="number" min="1" max="31"
            className="h-7 text-xs border rounded px-2 w-16"
            value={fc.day || 1}
            onChange={e => set('day', Math.min(31, Math.max(1, parseInt(e.target.value)||1)))}
          />
        </div>
        <AllDayOrTime fc={fc} set={set} />
      </div>
    );

    case 'custom': return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Every</span>
          <input type="number" min="1" max="90"
            className="h-7 text-xs border rounded px-2 w-16"
            value={fc.interval || 1}
            onChange={e => set('interval', Math.min(90, Math.max(1, parseInt(e.target.value)||1)))}
          />
          <Select value={fc.unit || 'day'} onValueChange={v => set('unit', v)}>
            <SelectTrigger className="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Day(s)</SelectItem>
              <SelectItem value="week">Week(s)</SelectItem>
              <SelectItem value="month">Month(s)</SelectItem>
              <SelectItem value="year">Year(s)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <AllDayOrTime fc={fc} set={set} />
      </div>
    );

    default: return null;
  }
}

const EMPTY_FORM = {
  category: 'Medical', type: 'Vaccination', name: '',
  dosage: '', dosage_unit: '', units: '',
  serving_size: '', serving_size_units: '',
  routine: false, frequency_type: 'daily', frequency_config: { ...FREQ_DEFAULTS.daily },
  end_date: '', end_date_type: 'fixed', end_date_rel_value: '', end_date_rel_unit: 'days',
  billable: false, cost: '', cost_currency: 'USD', notes: '',
  is_breeding: false,
  // Pricing structure fields
  pricing_type: 'vendor_cost',
  account_rate: '',
  internal_cost: '',
  cost_source: 'farm_paid',
  markup_strategy: 'account_default',
  custom_markup_percent: '',
  is_billable: false,
};

export default function CareCatalogPanel({ onClose, onSaved, horse }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const confirm = useConfirm();
  const [filterBreeding, setFilterBreeding] = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [showProtocols, setShowProtocols]   = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [tenantCurrency, setTenantCurrency] = useState('USD');

  // Fetch tenant default currency once on mount
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    if (user?.tenant_id) {
      api.get(`/tenants/${user.tenant_id}`)
        .then(res => {
          const cur = res.data?.default_currency || 'USD';
          setTenantCurrency(cur);
          setForm(f => ({ ...f, cost_currency: cur }));
        })
        .catch(() => {});
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/farmfolio/care-catalog');
      setItems(res.data || []);
    } catch { toast.error('Failed to load care catalog'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = () => {
    setEditing(null);
    const cat = CATALOG_CATEGORIES.includes(activeCategory) ? activeCategory : 'Medical';
    setForm({ ...EMPTY_FORM, category: cat, type: CARE_TYPES[cat][0], cost_currency: tenantCurrency });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      category: item.category, type: item.type, name: item.name,
      dosage: item.dosage || '', dosage_unit: item.dosage_unit || '',
      units: item.units ?? '',
      serving_size: item.serving_size ?? '', serving_size_units: item.serving_size_units || '',
      routine: item.routine || false,
      frequency_type: item.frequency_type || 'daily',
      // Merge saved config WITH defaults so partial/old configs get missing keys filled in
      frequency_config: { ...FREQ_DEFAULTS[item.frequency_type || 'daily'], ...(item.frequency_config || {}) },
      billable: item.billable || false,
      cost: item.cost ?? '', cost_currency: item.cost_currency || 'USD', notes: item.notes || '',
      is_breeding: item.is_breeding || false,
      pricing_type: item.pricing_type || 'vendor_cost',
      account_rate: item.account_rate ?? '',
      internal_cost: item.internal_cost ?? item.cost ?? '',
      cost_source: item.cost_source || 'farm_paid',
      markup_strategy: item.markup_strategy || 'account_default',
      custom_markup_percent: item.custom_markup_percent ?? '',
      is_billable: item.is_billable ?? item.billable ?? false,
    });
    setDialogOpen(true);
  };

  const openDuplicate = (item) => {
    setEditing(null);
    setForm({
      category: item.category, type: item.type, name: item.name + ' (Copy)',
      dosage: item.dosage || '', dosage_unit: item.dosage_unit || '',
      units: item.units ?? '',
      serving_size: item.serving_size ?? '', serving_size_units: item.serving_size_units || '',
      routine: item.routine || false,
      frequency_type: item.frequency_type || 'daily',
      frequency_config: { ...FREQ_DEFAULTS[item.frequency_type || 'daily'], ...(item.frequency_config || {}) },
      billable: item.billable || false, cost: item.cost ?? '', cost_currency: item.cost_currency || 'USD', notes: item.notes || '',
      is_breeding: item.is_breeding || false,
      pricing_type: item.pricing_type || 'vendor_cost',
      account_rate: item.account_rate ?? '',
      internal_cost: item.internal_cost ?? item.cost ?? '',
      cost_source: item.cost_source || 'farm_paid',
      markup_strategy: item.markup_strategy || 'account_default',
      custom_markup_percent: item.custom_markup_percent ?? '',
      is_billable: item.is_billable ?? item.billable ?? false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (item) => {
    if (!await confirm({ title: `Remove "${item.name}" from the care catalog?`, destructive: true })) return;
    try {
      await api.delete(`/farmfolio/care-catalog/${item.id}`);
      toast.success('Item removed');
      fetchItems();
      if (onSaved) onSaved();
    } catch { toast.error('Failed to remove'); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        units: form.units !== '' ? parseFloat(form.units) : null,
        serving_size: form.serving_size !== '' ? parseFloat(form.serving_size) : null,
        cost: form.pricing_type === 'vendor_cost' ? (form.internal_cost !== '' ? parseFloat(form.internal_cost) : null) : null,
        internal_cost: form.internal_cost !== '' ? parseFloat(form.internal_cost) : null,
        account_rate: form.account_rate !== '' ? parseFloat(form.account_rate) : null,
        custom_markup_percent: form.custom_markup_percent !== '' ? parseFloat(form.custom_markup_percent) : null,
        is_billable: form.is_billable,
        billable: form.is_billable,
      };
      if (editing) {
        await api.put(`/farmfolio/care-catalog/${editing.id}`, payload);
        toast.success('Catalog item updated');
      } else {
        await api.post('/farmfolio/care-catalog', payload);
        toast.success('Catalog item added');
      }
      setDialogOpen(false);
      fetchItems();
      if (onSaved) onSaved();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const isMedical      = form.category === 'Medical';
  const isFeedOrWater  = form.category === 'Daily Care' && ['Feed', 'Water'].includes(form.type);

  const showDosage     = isMedical;
  const showServingSize= isFeedOrWater;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-base flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary" /> Care Catalog
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline"
            onClick={() => setShowProtocols(v => !v)}
            className={showProtocols ? 'bg-primary text-white hover:bg-primary/90' : ''}>
            <Workflow className="w-4 h-4 mr-1" /> Protocols
          </Button>
          {!showProtocols && (
            <Button size="sm" onClick={openAdd} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-1" /> Add Item
            </Button>
          )}
          {onClose && (
            <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
          )}
        </div>
      </CardHeader>

      {showProtocols ? (
        <CardContent className="pt-0">
          <ProtocolPanel
            onClose={() => setShowProtocols(false)}
            catalogItems={items}
            horse={horse}
          />
        </CardContent>
      ) : (
      <CardContent className="pt-0">
        <p className="text-xs text-muted-foreground mb-3">
          The Care Catalog defines all available care types for your horses. Billable items automatically create saved invoice items.
        </p>

        {/* Filter buttons + Export — same row, export right-justified */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex flex-wrap gap-1.5">
            {['All', ...CATALOG_CATEGORIES].map(c => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  activeCategory === c
                    ? (ACTIVE_TAB_COLORS[c]?.active || 'bg-primary text-white border-primary')
                    : (ACTIVE_TAB_COLORS[c]?.inactive || 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50')
                }`}
              >{c}</button>
            ))}
            <button
              onClick={() => setFilterBreeding(v => !v)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                filterBreeding
                  ? 'bg-pink-100 text-pink-700 border-pink-300'
                  : 'bg-pink-50 text-pink-400 border-pink-200 hover:bg-pink-100'
              }`}
              data-testid="catalog-filter-breeding"
            >Breeding</button>
          </div>
          <div className="relative flex-1 min-w-[100px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search…"
              className="h-7 text-xs pl-7 w-full"
            />
          </div>
          <Button size="sm" variant="outline" className="shrink-0" onClick={async () => {
            const displayItems = (activeCategory === 'All'
              ? [...items].sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name))
              : items.filter(i => i.category === activeCategory)
            ).filter(i => !filterBreeding || i.is_breeding).filter(i => !searchQuery || (i.name + ' ' + i.type).toLowerCase().includes(searchQuery.toLowerCase()));
            const showCategory = activeCategory === 'All';
            const headers = [...(showCategory ? ['Category'] : []), 'Type', 'Name', 'Routine', 'Frequency', 'Billable', 'Cost'];
            const rows = displayItems.map(item => {
              let nameDisplay = item.name;
              if (item.dosage) nameDisplay += ` (${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''})`;
              else if (item.serving_size != null) nameDisplay += ` (${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''})`;
              return [
                ...(showCategory ? [item.category] : []),
                item.type, nameDisplay,
                item.routine ? 'Yes' : 'No',
                formatFreq(item),
                item.billable ? 'Yes' : 'No',
                item.cost != null ? `${item.cost_currency || 'USD'} ${Number(item.cost).toFixed(2)}` : '',
              ];
            });
            const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `care_catalog_${activeCategory.toLowerCase().replace(/ /g, '_')}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}>
            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
          </Button>
        </div>

        {/* Unified table — same columns for all categories */}
        {(() => {
          const displayItems = (activeCategory === 'All'
            ? [...items].sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name))
            : items.filter(i => i.category === activeCategory)
          ).filter(i => !filterBreeding || i.is_breeding).filter(i => !searchQuery || (i.name + ' ' + i.type).toLowerCase().includes(searchQuery.toLowerCase()));

          if (loading) return <p className="text-sm text-center py-6 text-muted-foreground">Loading…</p>;
          if (displayItems.length === 0) return (
            <p className="text-sm text-center py-6 text-muted-foreground italic">
              No items yet. Click "Add Item" to create one.
            </p>
          );

          return (
            <Table>
              <TableHeader>
                <TableRow>
                  {activeCategory === 'All' && <TableHead className="text-sm font-semibold w-32">Category</TableHead>}
                  <TableHead className="text-sm font-semibold w-36">Type</TableHead>
                  <TableHead className="text-sm font-semibold max-w-[120px]">Name</TableHead>
                  <TableHead className="text-sm font-semibold w-20">Routine</TableHead>
                  <TableHead className="text-sm font-semibold w-72">Frequency</TableHead>
                  <TableHead className="text-sm font-semibold w-20">Billable</TableHead>
                  <TableHead className="text-sm font-semibold w-24">Cost</TableHead>
                  <TableHead className="w-9"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayItems.map(item => {
                  // Append dosage or serving size in parentheses after the name
                  let nameDisplay = item.name;
                  if (item.dosage) {
                    nameDisplay += ` (${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''})`;
                  } else if (item.serving_size != null) {
                    nameDisplay += ` (${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''})`;
                  }

                  return (
                    <TableRow key={item.id}>
                      {activeCategory === 'All' && <TableCell className="text-sm text-muted-foreground">{item.category}</TableCell>}
                      <TableCell className="text-sm">{item.type}</TableCell>
                      <TableCell className="text-sm font-medium max-w-[120px] truncate">{nameDisplay}</TableCell>
                      <TableCell>
                        {item.routine
                          ? <Badge className="bg-green-100 text-green-700 text-xs">Yes</Badge>
                          : <span className="text-sm text-muted-foreground">No</span>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatFreq(item)}</TableCell>
                      <TableCell>
                        {(item.is_billable ?? item.billable)
                          ? <Badge className="bg-blue-100 text-blue-700 text-xs">Yes</Badge>
                          : <span className="text-sm text-muted-foreground">No</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.pricing_type === 'internal_service'
                          ? (item.account_rate != null ? `${item.cost_currency || 'USD'} ${Number(item.account_rate).toFixed(2)}` : '—')
                          : (item.internal_cost != null || item.cost != null)
                            ? `${item.cost_currency || 'USD'} ${Number(item.internal_cost ?? item.cost).toFixed(2)}`
                            : '—'}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} title="Edit">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          );
        })()}
      </CardContent>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Care Item' : 'Add Care Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v, type: CARE_TYPES[v][0] }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATALOG_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(CARE_TYPES[form.category] || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rabies Booster, Purina Strategy" />
            </div>

            {showDosage && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Dosage</Label>
                  <Input value={form.dosage} onChange={e => setForm(f => ({ ...f, dosage: e.target.value }))} placeholder="e.g. 100" />
                </div>
                <div className="space-y-1">
                  <Label>Dosage Units</Label>
                  <Select value={form.dosage_unit || ''} onValueChange={v => setForm(f => ({ ...f, dosage_unit: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map(u => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {showServingSize && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Serving Size</Label>
                  <Input type="number" min="0" step="0.1" value={form.serving_size}
                    onChange={e => setForm(f => ({ ...f, serving_size: e.target.value }))} placeholder="e.g. 2" />
                </div>
                <div className="space-y-1">
                  <Label>Serving Size Units</Label>
                  <Select value={form.serving_size_units || ''} onValueChange={v => setForm(f => ({ ...f, serving_size_units: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map(u => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch checked={form.routine} onCheckedChange={v => setForm(f => ({ ...f, routine: v }))} />
              <Label className="cursor-pointer">Routine item</Label>
            </div>

            {form.routine && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Frequency</Label>
                  <Select
                    value={form.frequency_type}
                    onValueChange={v => setForm(f => ({ ...f, frequency_type: v, frequency_config: { ...FREQ_DEFAULTS[v] } }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="p-2 border rounded bg-muted/20">
                  <FrequencyConfig form={form} setForm={setForm} />
                </div>

                {/* End Date — Fixed or Relative */}
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">End Date</Label>
                  <div className="flex gap-1 mb-1">
                    {['fixed','relative'].map(t => (
                      <button key={t} type="button"
                        onClick={() => setForm(f => ({ ...f, end_date_type: t }))}
                        className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                          form.end_date_type === t
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}>
                        {t === 'fixed' ? 'Fixed Date' : 'Relative'}
                      </button>
                    ))}
                  </div>
                  {form.end_date_type === 'fixed' ? (
                    <Input type="date" value={form.end_date}
                      onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                      className={`h-7 text-xs ${form.end_date ? 'time-filled' : ''}`} />
                  ) : (
                    <div className="flex gap-2 items-center">
                      <Input type="number" min="1" step="1" value={form.end_date_rel_value}
                        onChange={e => setForm(f => ({ ...f, end_date_rel_value: e.target.value }))}
                        placeholder="e.g. 30" className="h-7 text-xs w-20" />
                      <Select value={form.end_date_rel_unit}
                        onValueChange={v => setForm(f => ({ ...f, end_date_rel_unit: v }))}>
                        <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['days','weeks','months','years'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">from start</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Pricing Structure ──────────────────────────────── */}
            <div className="rounded-lg border border-muted bg-muted/30 p-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pricing &amp; Cost</p>
              <div className="flex gap-1">
                {[
                  { value: 'internal_service', label: 'Internal Service' },
                  { value: 'vendor_cost', label: 'Vendor Cost' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setForm(f => ({ ...f, pricing_type: opt.value }))}
                    className={`flex-1 px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                      form.pricing_type === opt.value
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >{opt.label}</button>
                ))}
              </div>

              {form.pricing_type === 'internal_service' ? (
                <div key="internal" className="space-y-1">
                  <Label className="text-xs">Account Rate</Label>
                  <div className="flex gap-2">
                    <Select value={form.cost_currency || tenantCurrency} onValueChange={v => setForm(f => ({ ...f, cost_currency: v }))}>
                      <SelectTrigger className="w-20 shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>{['USD','EUR','GBP','CAD','AUD','NZD','ZAR'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" min="0" step="0.01" value={form.account_rate}
                      onChange={e => setForm(f => ({ ...f, account_rate: e.target.value }))}
                      placeholder="0.00" className="flex-1" />
                  </div>
                  <p className="text-xs text-muted-foreground">Price charged to the account. No markup applied.</p>
                </div>
              ) : (
                <div key="vendor" className="space-y-3">
                  {/* Internal Base Cost */}
                  <div className="space-y-1">
                    <Label className="text-xs">Internal Base Cost</Label>
                    <div className="flex gap-2">
                      <Select value={form.cost_currency || tenantCurrency} onValueChange={v => setForm(f => ({ ...f, cost_currency: v }))}>
                        <SelectTrigger className="w-20 shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent>{['USD','EUR','GBP','CAD','AUD','NZD','ZAR'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" min="0" step="0.01" value={form.internal_cost}
                        onChange={e => setForm(f => ({ ...f, internal_cost: e.target.value }))}
                        placeholder="0.00" className="flex-1" />
                    </div>
                  </div>
                  {/* Cost Source */}
                  <div className="space-y-1">
                    <Label className="text-xs">Cost Source</Label>
                    <Select value={form.cost_source} onValueChange={v => setForm(f => ({ ...f, cost_source: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="farm_paid">Farm Paid</SelectItem>
                        <SelectItem value="direct_vendor_billed">Direct Billed by Vendor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Markup Strategy — only for farm_paid AND billable */}
                  {form.cost_source === 'farm_paid' && form.is_billable && (
                    <div className="space-y-1">
                      <Label className="text-xs">Markup Strategy</Label>
                      <Select value={form.markup_strategy} onValueChange={v => setForm(f => ({ ...f, markup_strategy: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tenant_default">Apply Tenant Default Markup</SelectItem>
                          <SelectItem value="item_custom">Apply Custom Item Markup (%)</SelectItem>
                          <SelectItem value="account_default">Use Account Markup</SelectItem>
                          <SelectItem value="none">No Markup (Pass-Through Cost)</SelectItem>
                        </SelectContent>
                      </Select>
                      {form.markup_strategy === 'item_custom' && (
                        <Input type="number" min="0" max="100" step="0.1" value={form.custom_markup_percent}
                          onChange={e => setForm(f => ({ ...f, custom_markup_percent: e.target.value }))}
                          placeholder="e.g. 20" className="h-8 text-sm mt-1" />
                      )}
                    </div>
                  )}
                  {!form.is_billable && (
                    <p className="text-xs text-muted-foreground">Cost will pass through as $0 on invoices.</p>
                  )}
                </div>
              )}

              {/* Billable toggle — always shown */}
              <div className="flex items-center gap-2">
                <Switch checked={form.is_billable} onCheckedChange={v => setForm(f => ({ ...f, is_billable: v, billable: v }))} />
                <Label className="cursor-pointer">Billable to account</Label>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.is_breeding} onCheckedChange={v => setForm(f => ({ ...f, is_breeding: v }))} />
              <Label className="cursor-pointer">Breeding-related care</Label>
            </div>
          </div>
          <div className="flex flex-col gap-2 pt-2 border-t mt-2">
            {/* Row 1 — Duplicate (only when editing) */}
            {editing && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => { openDuplicate(editing); }}
                  disabled={saving}
                  title="Duplicate this item"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
                </Button>
              </div>
            )}
            {/* Row 2 — Trash icon left, Cancel + Save right */}
            <div className="flex items-center justify-between w-full">
              <div>
                {editing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={async () => { handleDelete(editing); setDialogOpen(false); }}
                    disabled={saving}
                    title="Delete this item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
